import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { FileService } from '@core/services/file.service';
import {
  FileConfig,
  FileUpload,
  IFileConfig,
  IValidationMessage,
} from '@models/file';
import { RequiredValidation } from '@utils/validation/required-validation';
import { UploadValidation } from '@utils/validation/upload-validation';

@Component({
  selector: 'app-image-uploader',
  template: `
    <app-base-uploader
      [currentImageFile]="currentImage"
      [fileConfig]="fileConfig"
      [fileListFormControl]="fileListFormControl"
      [form]="form"
      [validationMessageList]="validationMessageList"
      (emitFileSelect)="onFileSelect($event)"
      (fileUploadsEmitter)="fileUploadsEmitter.emit($event)"
      [required]="required"
    ></app-base-uploader>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageUploaderComponent implements OnInit {
  @Input() public currentImage: string;
  @Input() public fileListFormControlName: string = 'formValue';
  @Input() public required: boolean = false;
  /**
   * The uploader requires the parent component to set up the form that
   * will contain the `FormControl` for the `FileList`.
   */
  @Input() public form: UntypedFormGroup;
  @Output() fileUploadsEmitter: EventEmitter<FileUpload[]> = new EventEmitter();

  public fileConfig = new FileConfig({
    allowedFileExtensionList: ['png', 'jpg', 'jpeg', 'gif'],
    allowedMimeTypeList: ['image/*'],
    canUploadMultiple: false,
  });
  public validationMessageList: IValidationMessage[] = [];

  constructor(
    private fileService: FileService,
    private fb: UntypedFormBuilder,
  ) {}

  public ngOnInit(): void {
    if (!this.form) {
      this.form = this.fb.group({
        formValue: new UntypedFormControl('', Validators.required),
      });
    }
    this.setFileListFormControlValidation(this.fileConfig);
  }

  public get fileListFormControl(): AbstractControl {
    return this.form.get(this.fileListFormControlName);
  }

  public get fileListFormControlHidden(): AbstractControl {
    return this.form.get(`${this.fileListFormControlName}HiddenField`);
  }

  public onFileSelect(fileList: FileList): void {
    this.validationMessageList = this.validateFiles(fileList);
    this.fileListFormControl.setValue(fileList);
    this.fileListFormControlHidden?.setValue(
      fileList?.length ? fileList[0].name : this.currentImage,
    );
  }

  private setFileListFormControlValidation(fileConfig: IFileConfig): void {
    const existingValidator = this.fileListFormControl?.validator;
    const validatorList = [UploadValidation.validateFiles(fileConfig)];

    if (existingValidator) validatorList.push(existingValidator);

    this.fileListFormControl?.setValidators(validatorList);
    this.fileListFormControl?.updateValueAndValidity();
  }

  private validateFiles(fileList: FileList): IValidationMessage[] {
    return this.fileService.buildValidationSuccessMessageList(
      fileList,
      this.fileConfig,
    );
  }
}
